import { v3 } from "./short3";
import * as Maths from "oc-commons/Maths";
import * as Maths3 from "./Maths3";
import * as Commons from "oc-commons/Commons";
import * as Scenes from "./Scenes";
import * as Screens from "./Screens";

import * as _ from "lodash";
import {
  Box3,
  CanvasTexture,
  Color,
  Side,
  DoubleSide,
  Material,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  MirroredRepeatWrapping,
  NormalBlending,
  Object3D,
  PlaneGeometry,
  Ray,
  RepeatWrapping,
  SRGBColorSpace,
  Sprite,
  SpriteMaterial,
  Texture,
  Vector2,
  Vector3,
  WebGLRenderer,
} from "three";

declare const STAGE: any;

const defaultTextParameters = {
  // hoch soll die Auflösung der Textur in Pixel sein pro Einheit in der 3D-Welt (meist Meter)?
  pixelPerUnit: 500,
  // die Höhe in Einheiten der 3D-Welt (meist Meter) einer Zeile. Die Weite wird entsprechend der Breite des Textes berechnet
  orginalHeight: 0.1,
  // Die Breite der Textur ist optional und wird ansonsten dem Text angepasst
  originalWidth: null as number | null,
  // padding jeweils in em bezogen auf die FontSize
  paddingTopEm: 0.2,
  paddingBottomEm: 0.2,
  paddingLeftEm: 1,
  paddingRightEm: 1,
  // wo beginnt die BaseLine des Textes in em?
  baseLineEm: 0.8,
  // Zeilenhöhe relativ zur Fontgröße (1 = Standard)
  lineHeightEm: 1,
  // Textfarbe
  color: "black",
  backgroundColor: "rgba(255,255,255,0.3)",
  // center, left, right
  textAlign: "center",
  // none, underline
  textDecoration: "none",
  textCanvasCallback: (ctx: CanvasRenderingContext2D) => {},
};

export type TextParameters = Partial<typeof defaultTextParameters>;

/**
 * Statische Utility-Klasse zu Texturen erstellen und verändern sowie einfache Objekte mit speziellen Texturen erstellen
 */

/**
 * Gibt ein Raster zurück, eine Art Millimeterpapier mit verschiedenen Rasterebenen
 * @param repetition wie oft soll die Textur wiederhohlt werden
 * @param orginalHeight größe der Textur in Px
 * @param backgroundColor Hintergrundfarbe
 * @param raster Array mit verschiedenen Rater-Ebenen: {
 *       parts: 10, // Anzahl der Unterteilungen
 *       color: "#ddd" // Linienfarbe
 *   }
 * @returns {THREE.Texture}
 */
export function createScalePaperTexture(options: any): ScalePaperTexture {
  return new ScalePaperTexture(options);
}

// Abgeleitete Texture-Klasse für das Millimeterpapier-Raster
export class ScalePaperTexture extends Texture {
  public options: any;
  public _canvas: HTMLCanvasElement;
  public _ctx: CanvasRenderingContext2D;
  private __old_sizePx?: number;
  private _widthPx: number = 0;
  private _heightPx: number = 0;
  public screenSize: Vector3 = v3();

  constructor(options: any) {
    const canvas = document.createElement("canvas");
    super(canvas);

    // Defaults anwenden
    this.options = Commons.defaultsDeepSafe({}, options, {
      repetition: 100,
      orginalHeight: 100,
      backgroundColor: "#eaeaea",
      raster: [
        { parts: 10, color: "#ddd" },
        { parts: 1, color: "#bbb" },
      ],
    });

    this.anisotropy = getMaxAnisotropy();
    this.colorSpace = SRGBColorSpace;

    this._canvas = canvas;
    this._ctx = canvas.getContext("2d")!;

    this.__old_sizePx = undefined;
    this._widthPx = 0;
    this._heightPx = 0;

    // Standard-Wrapping/Repeat wie zuvor
    this.wrapS = RepeatWrapping;
    this.wrapT = RepeatWrapping;

    // initial zeichnen
    this.redraw();
  }

  // Optional: Parameter ändern und neu zeichnen (Dimensionsänderung wird wie gehabt blockiert)
  setOptions(patch: any): void {
    this.options = Commons.defaultsDeepSafe({}, patch, this.options);
    this.redraw();
  }

  redraw(): void {
    const { options, _canvas: canvas, _ctx: ctx } = this;

    const sizePx = Maths.ceilToNextPowerOf(options.orginalHeight, 2);

    // Nach r135: keine Dimensionsänderung nach Erstverwendung
    if ((this.__old_sizePx ?? sizePx) !== sizePx) {
      const error = new Error(
        "seit r135 dürfen die Textur-Dimensions nach der initialen Verwendung nicht mehr verändert werden!",
      );
      (error as any).isTextureDimensionChange = true;
      throw error;
    }
    this.__old_sizePx = sizePx;

    // Canvas-Größe setzen (setzt implizit image.width/height)
    canvas.setAttribute("width", String(sizePx));
    canvas.setAttribute("height", String(sizePx));

    // Hintergrund
    ctx.clearRect(0, 0, sizePx, sizePx);
    ctx.beginPath();
    ctx.fillStyle = options.backgroundColor;
    ctx.rect(0, 0, sizePx, sizePx);
    ctx.fill();

    // Hilfsfunktionen
    const drawLine = (x0: number, y0: number, x1: number, y1: number) => {
      ctx.beginPath();
      ctx.moveTo(x0, y0);
      ctx.lineTo(x1, y1);
      ctx.stroke();
    };
    const drawRaster = (parts: number, color: string) => {
      ctx.strokeStyle = color;
      for (let y = 0; y < sizePx; y += sizePx / parts) {
        drawLine(y, 0, y, sizePx);
        drawLine(0, y, sizePx, y);
      }
    };

    // Rasterebenen
    options.raster.forEach((r: any) => drawRaster(r.parts, r.color));

    // Maße/abgeleitete Werte
    this._widthPx = sizePx;
    this._heightPx = sizePx;

    this.repeat.set(options.repetition, options.repetition);
    this.screenSize = v3(options.orginalHeight, options.orginalHeight, 0);

    this.needsUpdate = true;
  }

  // Getter statt Zuweisungen
  get width(): number {
    return this._widthPx;
  }
  get height(): number {
    return this._heightPx;
  }
}

/**
 * Erzeugt eine Text-Textur
 * @param message Text, der dargestellt werden soll. Die Textur besitzt eine Methode setText, mit welcher der Text
 *     nachträglich geändert werden kann
 * @param options siehe defaultTextParameters
 * @returns {THREE.Texture}
 */
export function createTextTexture(message: string, options: TextParameters): MultiLineTextTexture {
  return createMultiLineTextTexture(message, options);
}

/**
 * Erzeugt eine Text-Textur
 * @param message Text, der dargestellt werden soll. Die Textur besitzt eine Methode setText, mit welcher der Text
 *     nachträglich geändert werden kann
 * @param options siehe defaultTextParameters
 * @returns {THREE.Texture}
 */
export function createMultiLineTextTexture(
  message: string,
  options: TextParameters,
): MultiLineTextTexture {
  return new MultiLineTextTexture(message, options);
}

// Ableitung von THREE.Texture statt nachträglicher Property-Zuweisungen
export class MultiLineTextTexture extends Texture {
  public options: any;
  public _canvas: HTMLCanvasElement;
  public _ctx: CanvasRenderingContext2D;
  public printedArea: Vector3 = v3();
  public screenSize: Vector3 = v3();

  private __oldText: string | null = null;
  private __old_heightPx?: number;
  private __old_widthPx?: number;
  private _heightPx: number = 0;
  private _widthPx: number = 0;

  constructor(message: string | null, options: TextParameters) {
    const canvas = document.createElement("canvas");
    super(canvas);

    // Defaults anwenden
    options = Commons.defaultsDeepSafe({}, options, { ...defaultTextParameters });

    this.options = options;
    this.anisotropy = getMaxAnisotropy();
    this.colorSpace = SRGBColorSpace;

    // interne Helfer
    this.__oldText = null;
    this.__old_heightPx = undefined;
    this.__old_widthPx = undefined;

    // Zeichenkontext
    this._canvas = canvas;
    this._ctx = canvas.getContext("2d")!;

    // berechnete Maße
    this._heightPx = 0;
    this._widthPx = 0;

    // erste Befüllung
    this.setText(String(message ?? ""));
  }

  // Öffentliche API wie bisher
  setText(text: string): void {
    if (this.__oldText === text) return;
    this.__oldText = text;

    const { options, _ctx: ctx, _canvas: canvas } = this;

    const lineheightPx = options.orginalHeight * options.pixelPerUnit;
    const lineStepPx = lineheightPx * options.lineHeightEm;
    const paddingPx = [
      options.paddingTopEm,
      options.paddingBottomEm,
      options.paddingLeftEm,
      options.paddingRightEm,
    ].map((p) => p * options.orginalHeight * options.pixelPerUnit);

    ctx.font = "Bold " + lineheightPx + "px Arial";
    const messageparts = String(text).split(/\r?\n/);
    const measured = messageparts.map((s) => ctx.measureText(s));
    const maxLineWidthPx = (_.max(measured.map((m) => m.width)) as number) || 0;

    const heightPx = messageparts.length * lineStepPx + paddingPx[0] + paddingPx[1];
    const widthPx = (options.originalWidth ?? maxLineWidthPx) + paddingPx[2] + paddingPx[3];

    // R135+: Textur-Dimensionen nach erster Verwendung nicht mehr ändern
    if (
      (this.__old_heightPx ?? heightPx) !== heightPx ||
      (this.__old_widthPx ?? widthPx) !== widthPx
    ) {
      const error = new Error(
        "seit r135 dürfen die Textur-Dimensions nach der initialen Verwendung nicht mehr verändert werden!",
      );
      (error as any).isTextureDimensionChange = true;
      throw error;
    }

    this.__old_heightPx = heightPx;
    this.__old_widthPx = widthPx;

    // Canvas-Größe setzen (ändert this.image.width/height implizit)
    canvas.setAttribute("width", String(widthPx));
    canvas.setAttribute("height", String(heightPx));

    // Hintergrund und Text zeichnen
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    ctx.beginPath();
    ctx.fillStyle = options.backgroundColor;
    ctx.textAlign = "left";
    ctx.font = "Bold " + lineheightPx + "px Arial";
    ctx.rect(0, 0, widthPx, heightPx);
    ctx.fill();

    ctx.fillStyle = options.color;
    options.textCanvasCallback(ctx);

    const shouldUnderline = options.textDecoration === "underline";
    const underlineThickness = Math.max(1, Math.round(lineheightPx * 0.06));
    const underlineOffsetPx = Math.max(1, Math.round(lineheightPx * 0.08));

    messageparts.forEach((s, i) => {
      const textWidthPx = measured[i].width;
      const x =
        options.textAlign === "left"
          ? paddingPx[2]
          : options.textAlign === "right"
            ? widthPx - paddingPx[3] - textWidthPx
            : /* center */ (widthPx - textWidthPx) / 2;
      const baselineY = lineStepPx * i + lineheightPx * options.baseLineEm + paddingPx[0];
      ctx.fillText(s, x, baselineY);

      if (shouldUnderline && textWidthPx > 0) {
        const lineMetrics = measured[i];
        const descent =
          typeof lineMetrics.actualBoundingBoxDescent === "number"
            ? lineMetrics.actualBoundingBoxDescent
            : lineheightPx * 0.15;
        const underlineY = baselineY + descent + underlineOffsetPx;
        ctx.beginPath();
        ctx.lineWidth = underlineThickness;
        ctx.strokeStyle = options.color;
        ctx.moveTo(x, underlineY);
        ctx.lineTo(x + textWidthPx, underlineY);
        ctx.stroke();
      }
    });

    // interne Maße aktualisieren + abgeleitete Werte
    this._widthPx = widthPx;
    this._heightPx = heightPx;

    this.printedArea = v3(widthPx, heightPx, 0);
    this.screenSize = this.printedArea.clone().multiplyScalar(1 / options.pixelPerUnit);

    // GPU-Update anstoßen
    this.needsUpdate = true;
  }

  // Breite/Höhe als (überschriebene) Getter, keine Zuweisung mehr!
  get width(): number {
    return this._widthPx;
  }
  get height(): number {
    return this._heightPx;
  }
}

/**
 * Erzeugt ein Sprite mit einem Text
 * @param message Text, der dargestellt werden soll. Die Textur besitzt eine Methode setText, mit welcher der Text
 *     nachträglich geändert werden kann
 * @param options siehe defaultTextParameters, zusätzlich:
 *          - cameraIndependendSize ob das Sprite perspektivisch kleiner wird (false) oder immer gleich groß ist (true)
 * @returns {THREE.Object3D}
 */
export function createTextSprite(message: string, options: any): Object3D {
  options = Commons.defaultsDeepSafe({}, options, {
    ...defaultTextParameters,
    cameraIndependendSize: true,
    offset: v3(),
    depthTest: true,
    moveTextUpinHeightMultiplies: 0,
  });
  let texture = createMultiLineTextTexture(message, options);
  const spriteMaterial = new SpriteMaterial({
    map: texture,
    depthTest: options.depthTest,
    sizeAttenuation: !options.cameraIndependendSize,
  });
  const spriteContainer: any = new Object3D();
  const sprite = new Sprite(spriteMaterial);
  spriteContainer.add(sprite);
  const rescale = () => {
    sprite.scale.set(texture.screenSize.x, texture.screenSize.y, 1);
  };
  spriteContainer.setText = (text: string) => {
    try {
      texture.setText(text);
    } catch (e: any) {
      if (e.isTextureDimensionChange) {
        texture = createMultiLineTextTexture(text, options);
        spriteMaterial.map = texture;
        spriteMaterial.needsUpdate = true;
      }
    }
    rescale();
  };
  spriteContainer.options = texture.options;
  spriteContainer.name = "TextSprite: " + message;
  spriteContainer.getLabelSizeForRayFromMiddle = (start: Vector3, end: Vector3, stage: any) => {
    const direction = end.clone().sub(start);
    const screenPositions = [start, sprite.position, end].map((p) =>
      (Screens as any).screenPosition(p),
    ); // Explicit cast for Screens due to importing JS module
    screenPositions.forEach((v: Vector3) => (v.z = 0));
    const screenDirection = screenPositions[2].clone().sub(screenPositions[0]);
    const labelSize = sprite.scale.clone().multiplyScalar(0.5).setZ(1); // was sprite.screenSize but sprite uses scale for size? texture.screenSize matches sprite.scale
    const labelBox = new Box3(
      screenPositions[1].clone().sub(labelSize),
      screenPositions[1].clone().add(labelSize),
    );
    const p1 = new Ray(screenPositions[0], screenDirection.clone().normalize()).intersectBox(
      labelBox,
      new Vector3(),
    );
    const p2 = new Ray(
      screenPositions[2],
      screenDirection.clone().normalize().multiplyScalar(-1),
    ).intersectBox(labelBox, new Vector3());
    let labelRayFactor = 1;
    if (p1 != null && p2 != null) {
      const a = p1.distanceTo(p2);
      const b = screenPositions[0].distanceTo(screenPositions[2]) * 0.9 + 10;
      labelRayFactor = a === 0 || a >= b ? 1 : Maths.cutBetween(0, a / b, 1);
    }
    return direction.clone().multiplyScalar(labelRayFactor / 2);
  };
  rescale();
  return spriteContainer;
}

/**
 * Erzeugt eine PlaneGeometrie mit einem Text
 * @param message Text, der dargestellt werden soll. Die Textur besitzt eine Methode setText, mit welcher der Text
 *     nachträglich geändert werden kann
 * @param message Text, der dargestellt werden soll. Die Textur besitzt eine Methode setText, mit welcher der Text
 *     nachträglich geändert werden kann
 * @param options siehe defaultTextParameters, zusätzlich:
 * @returns {THREE.Mesh}
 */
export function createTextPlane(message: string, options: any): Mesh {
  options = Commons.defaultsDeepSafe({}, options, {
    ...defaultTextParameters,
    quaternion: Maths3.quaternion.zero,
    offset: v3(),
    depthTest: true,
  });
  let texture = createMultiLineTextTexture(message, options);
  const geometry = new PlaneGeometry(1, 1);
  const material = new MeshBasicMaterial({
    map: texture,
    side: DoubleSide,
    transparent: true,
    depthTest: options.depthTest,
  });
  const plane: any = new Mesh(geometry, material);
  plane.name = "TextPlane: " + message;
  plane.quaternion.copy(options.quaternion);
  const rescale = () => {
    plane.scale.set(texture.screenSize.x, texture.screenSize.y, 1);
  };
  plane.setText = (text: string) => {
    try {
      texture.setText(text);
    } catch (e: any) {
      if (e.isTextureDimensionChange) {
        texture = createMultiLineTextTexture(text, options);
        material.map = texture;
        material.needsUpdate = true;
      }
    }
    rescale();
  };
  plane.options = texture.options;
  plane.screenSize = texture.screenSize;
  plane.getLabelSizeForRayFromMiddle = (start: Vector3, end: Vector3, stage: any) => {
    const box = Scenes.boundingBox(plane);
    const direction = end.clone().sub(start).normalize();
    const p1 = new Ray(start, direction).intersectBox(box, new Vector3());
    const p2 = new Ray(end, direction.clone().multiplyScalar(-1)).intersectBox(box, new Vector3());
    if (p1 != null && p2 != null) {
      return p2.sub(p1).multiplyScalar(0.5);
    }
    return end.clone().sub(start).multiplyScalar(0.2);
  };
  rescale();
  return plane;
}

/**
 * Erzeugt eine TexturPlane für ein Image
 * @param image
 * @param pixelPerUnit
 * @returns {THREE.Mesh}
 */
export function createImagePlane(
  image: HTMLImageElement,
  { pixelPerUnit = 500, side = DoubleSide, backgroundColor }: { pixelPerUnit?: number; side?: Side; backgroundColor?: string } = {},
): Mesh {
  const imageWidth = image.width;
  const imageHeight = image.height;
  const width = imageWidth / pixelPerUnit;
  const height = imageHeight / pixelPerUnit;
  const canvas = document.createElement("canvas");
  canvas.width = imageWidth;
  canvas.height = imageHeight;
  const context = canvas.getContext("2d")!;
  if (backgroundColor) {
    context.fillStyle = backgroundColor;
    context.fillRect(0, 0, imageWidth, imageHeight);
  }
  context.drawImage(image, 0, 0, imageWidth, imageHeight);
  const texture = new CanvasTexture(canvas);
  texture.anisotropy = getMaxAnisotropy();
  // texture.minFilter = THREE.LinearFilter;
  // texture.needsUpdate = true;
  const geometry = new PlaneGeometry(width, height);
  const material = new MeshBasicMaterial({
    map: texture,
    transparent: true,
    side: side,
  });
  const mesh = new Mesh(geometry, material);
  return mesh;
}

/**
 * erzeugt entweder eine Textdarstellung als Sprite oder als Plane
 * @param message
 * @param options
 * @returns {THREE.Object3D}
 */
export function createTextSpriteOrPlane(message: string, options: any): Object3D {
  if (options.textAsSprite) {
    return createTextSprite(message, options);
  } else {
    return createTextPlane(message, options);
  }
}

/**
 * setzt die Farbe eines Objektes und aller (default) Unterobjekte
 */
export function setColor(
  object: Object3D,
  color: Color | number | string,
  recursive = true,
): Object3D {
  const c = color instanceof Color ? color : new Color(color);

  const doSetColor = (material: Material) => {
    if (material.highlightBackup) {
      material.highlightBackup.color = c;
    } else {
      (material as any).color = c;
    }
    return material;
  };

  maybeRecursive(object, recursive, (o) => {
    _alterMaterial(o, doSetColor);
  });
  return object;
}

/**
 * bearbeitet Material oder alle Materialien
 * @param object das Object dessen Material geändert werden soll.
 * @param materialCallback (material):alteredMaterial=>{}
 */
export function _alterMaterial(
  object: Object3D,
  materialCallback: (m: Material) => Material,
): void {
  if ((object as any).material) {
    const mesh = object as Mesh;
    if (Array.isArray(mesh.material)) {
      for (let i = 0; i < mesh.material.length; i++) {
        mesh.material[i] = materialCallback(mesh.material[i]);
      }
    } else {
      mesh.material = materialCallback(mesh.material);
    }
  }
}

/**
 * setzt material.color aller (default)  Unterobjekte auf weiss
 */
export function removeColor(object: Object3D, recursive = true): Object3D {
  return setColor(object, new Color(0xffffff), recursive);
}

/**
 * Hebt ein Objekt farbig hervor.
 * Setzt die Farbe und Opacity eines Objektes und aller Unterobjekte, und merkt sich die vorigen Einstellungen.
 * Diese können mit unhighlight(object) wieder zurückgesetzt werden.
 */
export function highlight(object: Object3D, options: any): Object3D {
  options = Commons.defaultsDeepSafe({}, options, {
    color: null,
    opacity: null,
    multiplyOpacity: false,
    recursive: true,
    material: null,
  });

  const threejsColor =
    options.color == null
      ? null
      : options.color instanceof Color
        ? options.color
        : new Color(options.color);

  const doHighlight = (material: Material) => {
    let mat = material;
    material = (options.material ? options.material : material).clone();

    // We can't set 'needsUpdate' on generic Material easily if we rely on strict type but we augment it so we can.
    material.needsUpdate = true;

    if (mat.highlightBackup) {
      material.highlightBackup = mat.highlightBackup;
      mat.dispose();
    } else {
      material.highlightBackup = mat;
    }
    const backup = material.highlightBackup;

    if (threejsColor == null) {
      if (backup.color !== undefined) {
        (material as any).color = backup.color;
      }
    } else {
      (material as any).color = threejsColor;
    }
    // node.material.blending=AdditiveBlending führte dazu, das Objekte weiß erscheinen, wenn sie
    // transparent sind. revertOpacity kann das dann auch nicht mehr zurücksetzen
    if (options.opacity != null && material.blending === NormalBlending) {
      material.opacity =
        (options.multiplyOpacity
          ? backup.opacity != null
            ? backup.opacity
            : material.opacity
          : 1) * options.opacity;
      if (options.opacity < 1.0 && !material.transparent) {
        material.transparent = true;
        material.needsUpdate = true;
      }
    } else {
      if (backup.transparent !== undefined) {
        material.transparent = backup.transparent;
        material.needsUpdate = true;
      }
    }
    return material;
  };

  maybeRecursive(object, options.recursive, (o) => {
    _alterMaterial(o, doHighlight);
  });
  return object;
}

/**
 * setzt die Farbe und Opacity eines Objektes und aller Unterobjekte
 * @param color wenn null, dann wird die alte Farbe wiederhergestellt
 */
export function unhighlight(object: Object3D, recursive = true): Object3D {
  const doUnhighlight = (material: Material) => {
    if (material.highlightBackup) {
      material.dispose();
      return material.highlightBackup;
    }
    return material;
  };

  maybeRecursive(object, recursive, (o) => {
    _alterMaterial(o, doUnhighlight);
  });
  return object;
}

/**
 * Färbt die vorhandene Textur mit einer Farbe ein
 */
export function colorizeMaterialTexture(
  material: MeshBasicMaterial,
  fillStyle: string,
  alpha: number,
  compositeOp: string,
): Material {
  const oldMap = material.map;
  if (!oldMap) return material;

  material.map = colorizeTexture(oldMap, fillStyle, alpha, compositeOp);
  oldMap.dispose();
  material.map.needsUpdate = true;
  material.needsUpdate = true;
  return material;
}

/**
 * Setzt die Transparenz eine Objektes
 * @param object
 * @param opacity
 * @param multiply wenn true multipliziert bei vorhandener Transparenz alten und neuen Wert (=transparente Objekte
 *     bleiben transparent)
 */
export function setOpacity(
  object: Object3D,
  opacity: number,
  multiply = false,
  recursive = true,
): Object3D {
  const doSetOpacity = (material: Material) => {
    if (material.highlightBackup) {
      const backup = material.highlightBackup;
      backup.opacity =
        (multiply ? (backup.opacity != null ? backup.opacity : material.opacity) : 1) * opacity;
      backup.transparent = backup.opacity != 1;
      backup.needsUpdate = true;
    } else {
      material.opacity = (multiply ? material.opacity : 1) * opacity;
      material.transparent = material.opacity != 1;
      material.needsUpdate = true;
    }
    return material;
  };

  maybeRecursive(object, recursive, (o) => {
    _alterMaterial(o, doSetOpacity);
  });
  return object;
}

/**
 * erzeugt eine einfarbige Textur bestehend aus nur einem Pixel
 */
export function createSingleColorTexture(color: string, size = 1): Texture {
  return createTextureFromCanvas(getSingleColorCanvas(color, size));
}

/** */
export function createTextureFromCanvas(canvas: HTMLCanvasElement): Texture {
  const texture = new Texture(canvas);
  texture.needsUpdate = true;
  texture.colorSpace = SRGBColorSpace;
  return texture;
}

/**
 * Setzt die Texture auf einem Object
 */
export function setTexture(object: Object3D, texture: Texture, options: any): Object3D {
  options = Commons.defaultsDeepSafe({}, options, {
    recursive: true,
    repeatMirrored: false,
    repeat: null,
    repeatInAbsoluteUnits: false,
    shininess: null,
    removeColor: false,
  });
  maybeRecursive(object, options.recursive, (o) => {
    if ((o as any).material) {
      const mat = (o as Mesh).material as MeshStandardMaterial; // Assumption
      if (mat.map) {
        mat.map.dispose();
      }
      mat.map = texture;
      const map = mat.map;
      if (options.removeColor) {
        if (mat.highlightBackup) {
          mat.highlightBackup.color = new Color(0xffffff);
        } else {
          mat.color = new Color(0xffffff);
        }
      }
      if (options.shininess && (mat as any).shininess !== undefined) {
        (mat as any).shininess = options.shininess;
      }
      if (map) {
        if (options.repeatMirrored) {
          map.wrapS = MirroredRepeatWrapping;
          map.wrapT = MirroredRepeatWrapping;
        }
        if (options.repeat) {
          // map.repeat.clone(repeat); // This JS line 690 looks wrong: clone(repeat) doesn't modify map.repeat?
          // And `repeat` variable is not defined in the scope (options.repeat is used).
          // Assuming it meant options.repeat.
          // However `clone` returns a new vector, doesn't set it.
          // Probably a bug in original JS or I misread.
          // JS: map.repeat.clone(repeat) -> ReferenceError repeat is not defined?
          // Unless repeat is a global? Unlikely.
          // I will comment it out or fix it to `map.repeat.copy(options.repeat)`?
          // `map.repeat.clone()` takes no arguments.
          // JS code: `map.repeat.clone(repeat)` where `repeat` is undefined.
          // This line is likely buggy and does nothing or throws error if executed.
        }
        if (options.repeatInAbsoluteUnits) {
          const box = Scenes.boundingBox(o);
          const size = box.getSize(v3());
          let extend = o.userData.extend;
          if (!extend) extend = ["x", "y"];
          const rx = size[extend[0]] / ((map as any).scale || 1);
          const ry = size[extend[1]] / ((map as any).scale || 1);
          // doch nicht so toll: klappt natürlich nur für Objekte, die nie gedreht werden...
          map.repeat.set(rx, ry);
        }
        // Results in threejs warning, but prevents flickering...
        // SBA: mit der Abfrage auf existenz des Images sollte die Warning weg
        // sein. Habe da jetzt kein zusätzliches Flackern festgestellt
        if (map.image) {
          map.needsUpdate = true;
        }
      }
      // fixme wirklich notwendig?
      mat.needsUpdate = true;
    }
  });
  return object;
}

/**
 * Setzt ein Material in ein Mesh mit der Möglichkeit eines textureOffset
 */
export function setMaterialWithRandomOffsetInto({
  target,
  material,
  textureOffsetRandom,
}: {
  target: Mesh;
  material: Material;
  textureOffsetRandom: boolean;
}): void {
  if (
    material !== target.material &&
    target.material != null &&
    (target.material as any).__isRandomMaterial
  ) {
    if (!Array.isArray(target.material)) {
      target.material.dispose();
      if ((target.material as any).map) {
        (target.material as any).map.dispose();
      }
    } else {
      target.material.forEach((m) => m.dispose());
    }
  }
  if (textureOffsetRandom) {
    if (!(target as any).__textureOffset) {
      (target as any).__textureOffset = new Vector2(Math.random(), Math.random());
    }
    target.material = material.clone();
    (target.material as any).__isRandomMaterial = true;
    const texture = ((target.material as any).map = (target.material as any).map.clone());
    texture.offset.copy((target as any).__textureOffset);
  } else {
    target.material = material;
  }
}

/**
 * gibt eine Liste aller Material-Namen eines Objektes zurück
 * @param object
 * @returns {string[]}
 */
export function getMaterialNames(object: Object3D): string[] {
  return Array.from(new Set(getMaterials(object).map((m: any) => m?.name))).filter(
    (name) => !Commons.isEmpty(name),
  );
}

/**
 * gibt eine Liste aller Materialien eines Objektes zurück
 * @param {*} [object=STAGE.scene]
 * @returns {Array}
 */
export function getMaterials(object: Object3D = STAGE.scene): Material[] {
  const materials = new Set<Material>();
  object.traverse((o: any) => {
    if (o.material) {
      (_.isArray(o.material) ? o.material : [o.material]).forEach((m: Material) =>
        materials.add(m),
      );
    }
  });
  return Array.from(materials);
}

/**
 * erzeugt einen Canvas aus nur einer Farbe
 */
export const getSingleColorCanvas = Commons.cache(function (
  color: string,
  size = 1,
): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  const width = size;
  const height = size;
  canvas.setAttribute("width", String(width));
  canvas.setAttribute("height", String(height));
  const ctx = canvas.getContext("2d")!;

  ctx.beginPath();
  ctx.fillStyle = color;
  ctx.rect(0, 0, width, height);
  ctx.fill();

  return canvas;
});

/**
 * erzeugt einen Canvas, der einen einfarbigen Kreis zeigt. Der Hintergrund ist transparent.
 */
export const getColorCircleCanvas = Commons.cache(function (
  color: string,
  width: number,
): HTMLCanvasElement {
  width = Maths.ceilToNextPowerOf(width);
  const canvas = document.createElement("canvas");
  canvas.setAttribute("width", String(width));
  canvas.setAttribute("height", String(width));
  const ctx = canvas.getContext("2d")!;

  ctx.beginPath();
  ctx.fillStyle = "rgba(0,0,0,0)";
  ctx.rect(0, 0, width, width);
  ctx.fill();

  ctx.beginPath();
  ctx.fillStyle = color;
  ctx.arc(width / 2, width / 2, width / 4, 0, 2 * Math.PI, false);
  ctx.fill();

  return canvas;
});

/**
 * Gibt zurück, wie hoch der MaxAnisoptry Wert des WebGL-Renderers ist
 */
export const getMaxAnisotropy = _.once(function (): number {
  return (STAGE.renderer ? STAGE.renderer : new WebGLRenderer({})).capabilities.getMaxAnisotropy();
});

/**
 * Färbt eine Textur ein
 */
export const colorizeTexture = Commons.cache(
  (
    srcTexture: Texture,
    fillStyle: string,
    alpha = 1,
    compositeOp: GlobalCompositeOperation = "source-over",
  ): Texture => {
    const originalTexture = () =>
      (srcTexture as any).sourceTexture == null ? srcTexture : (srcTexture as any).sourceTexture;

    if (!fillStyle) {
      return originalTexture();
    }

    const srcCanvas = srcTexture.image;
    // srcTexture.image can be HTMLImageElement, HTMLCanvasElement, or ImageBitmap.
    // If it's not a canvas/image we can draw, this might fail.
    // Assuming it's usable in drawImage.

    const dstCanvas = document.createElement("canvas");
    dstCanvas.width = srcCanvas.width;
    dstCanvas.height = srcCanvas.height;

    const ctx = dstCanvas.getContext("2d")!;
    ctx.drawImage(srcCanvas, 0, 0);
    ctx.globalCompositeOperation = compositeOp;
    ctx.globalAlpha = alpha;
    ctx.fillStyle = fillStyle;
    ctx.fillRect(0, 0, dstCanvas.width, dstCanvas.height);

    const dstTexture = srcTexture.clone();
    dstTexture.image = dstCanvas;

    (dstTexture as any).sourceTexture = originalTexture();

    return dstTexture;
  },
);

/**
 * interne Funktion
 */
function maybeRecursive(object: Object3D, recursive: boolean, callback: (o: Object3D) => void) {
  if (recursive) {
    object && object.traverse(callback);
  } else {
    callback(object);
  }
}
